// Yeni e-posta ekranı için yardımcılar.

// Taslak anahtarı: backend `^[A-Za-z0-9-]{8,64}$` ile doğrular (IMAP komutuna girdiği için).
export const newDraftKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

// Sadece ASCII e-posta adresleri chip'e dönüşür (SMTP/EmailStr uyumu)
export const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && /^[\x00-\x7F]+$/.test(value);

export const escapeHtml = (text) =>
  (text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Düz metni (yanıt/ilet alıntısı) editör için HTML'e çevir: her satır bir paragraf
export const textToHtml = (text) =>
  (text || '')
    .split('\n')
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join('');

// HTML -> düz metin. GÜVENLİK: innerHTML ile ayrıştırma YAPMA; ayrışmış (detached) bir öğeye atanan
// <img onerror=...> bile tetiklenir. DOMParser etkisiz bir belge üretir (script çalışmaz, görsel yüklenmez).
// Gelen mail HTML'i bu fonksiyondan geçer (yanıt/ilet alıntısı).
export const htmlToPlainText = (html) => {
  if (!html) return '';
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6]|blockquote|pre)>/gi, '$&\n');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  doc.querySelectorAll('script, style, head').forEach((el) => el.remove());
  return (doc.body?.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};

export const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${parseFloat((bytes / 1024 ** i).toFixed(1)).toLocaleString('tr-TR')} ${units[i]}`;
};

// base64 uzunluğundan bayt tahmini
export const base64Bytes = (content) => Math.floor(((content || '').length * 3) / 4);

// Farklı kaynaklardan gelen ekleri tek şekle getir: { id, name, type, content, bytes, size }
// (ilet: {name, content, type}; taslak: {filename, content_type, size, content}; yeni dosya: File'dan)
export const normalizeAttachment = (a) => {
  const bytes = a.bytes ?? base64Bytes(a.content);
  return {
    id: a.id ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    name: a.name || a.filename || 'ek',
    type: a.type || a.content_type || 'application/octet-stream',
    content: a.content,
    bytes,
    size: formatBytes(bytes),
  };
};

export const fileToAttachment = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(
        normalizeAttachment({
          name: file.name,
          type: file.type,
          bytes: file.size,
          content: String(reader.result).split(',')[1],
        }),
      );
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// API'ye giden ek biçimi
export const toApiAttachments = (attachments) =>
  attachments.length
    ? attachments.map((a) => ({ filename: a.name, content: a.content, content_type: a.type }))
    : null;

// Linkler için güvenli protokol: http(s), mailto, tel. Protokolsüz ise https:// eklenir.
export const normalizeUrl = (value) => {
  const url = (value || '').trim();
  if (!url) return null;
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return null; // javascript:, data: vb. reddedilir
  return `https://${url}`;
};

export const formatDateTime = (iso) =>
  new Date(iso).toLocaleString('tr-TR', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

// "Göndermeyi zamanla" hazır seçenekleri (yerel saat)
export const getSchedulePresets = (now = new Date()) => {
  const at = (date, hour) => {
    const d = new Date(date);
    d.setHours(hour, 0, 0, 0);
    return d;
  };
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const nextMonday = new Date(now);
  nextMonday.setDate(now.getDate() + (((8 - now.getDay()) % 7) || 7));
  return [
    { id: 'tomorrow-am', label: 'Yarın sabah', date: at(tomorrow, 9) },
    { id: 'tomorrow-pm', label: 'Yarın öğleden sonra', date: at(tomorrow, 14) },
    { id: 'monday-am', label: 'Pazartesi sabah', date: at(nextMonday, 9) },
  ];
};

// <input type="datetime-local"> için yerel zaman biçimi
export const toLocalInputValue = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// E-posta istemcileri <p>'ye farklı varsayılan boşluk verir; editördeki görünümle aynı olsun
// diye her paragrafa satır içi margin:0 verilir, boş paragraflar satır boşluğu olarak korunur.
export const emailizeHtml = (html) =>
  (html || '')
    .replace(/<p>/g, '<p style="margin:0">')
    .replace(/<p style="margin:0"><\/p>/g, '<p style="margin:0"><br></p>');

export const EMAIL_BASE_STYLE = 'font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#1f2937';
